// ── My Strategy Vault ─────────────────────────────────────────────────

// Sparkline + format helpers come from lib/utils.js (window.formatPct etc.)
const pct = (v, dp = 1) => window.formatPct(v, dp);
const num = (v, dp = 2) => window.GQ.utils.formatNumber(v, dp);

function StratRow({ s, onToast, onNavigate, onPublish, onDelete }) {
  const statusBadge = { active:"badge-green", licensed:"badge-purple", listed:"badge-cyan", saved:"badge-muted" }[s.status]||"badge-muted";
  return (
    <div style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10, padding:16, marginBottom:10 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:12, flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:200 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
            <span style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)" }}>{s.name}</span>
            <span className={`badge ${statusBadge}`}>{s.status}</span>
          </div>
          <div style={{ display:"flex", gap:18, flexWrap:"wrap" }}>
            {[
              {l:s.cat==="earning"?"Revenue":"Return", v:s.cat==="earning"?s.rev:s.perf, c:s.color},
              {l:"Trades", v:s.trades},
              {l:"Win rate", v:s.winRate+"%"},
              ...(s.subs?[{l:"Subscribers",v:s.subs,c:"var(--accent)"}]:[]),
            ].map((kv,i)=>(
              <div key={i}>
                <div style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{kv.l}</div>
                <div style={{ fontFamily:"var(--font-mono)", fontSize:13, color:kv.c||"var(--ink)" }}>{kv.v}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ width:90, height:36, flexShrink:0 }}>
          <Sparkline
            data={s.sparkData && s.sparkData.length ? s.sparkData : window.sparkFromSeed(s.id.charCodeAt(0)+(s.id.charCodeAt(1)||0))}
            color={s.color}
            height={36}
          />
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{
            if (s.latestRunId) window.navigateTo("tearsheet", { run: s.latestRunId });
            else onNavigate?.("tearsheet");
          }}><FileText size={11}/> Tearsheet</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>{
            if (s.realId) window.navigateTo("strategy-detail", { id: s.realId });
            else onToast?.("Mint or load a strategy first.");
          }}><BarChart2 size={11}/> Detail</button>
          <button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("optimize")}><Cpu size={11}/> Optimize</button>
          {s.realId && (
            <button className="btn btn-ghost btn-sm" onClick={()=>onPublish?.(s)} title="List on marketplace">
              <Store size={11}/> Publish
            </button>
          )}
          {s.realId && (
            <button className="btn btn-danger btn-sm" title="Delete strategy" onClick={()=>onDelete?.(s)}>
              <Trash size={11}/>
            </button>
          )}
        </div>
      </div>
      <div style={{ display:"flex", gap:18, marginTop:10, paddingTop:10, borderTop:"1px solid var(--border)" }}>
        {[{l:"Sharpe",v:s.sharpe},{l:"Max DD",v:s.dd,c:"var(--red)"},{l:"30D",v:s.d30,c:"var(--green)"}].map(m=>(
          <div key={m.l} style={{ display:"flex", gap:5, alignItems:"baseline" }}>
            <span style={{ fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>{m.l}</span>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:12, color:m.c||"var(--ink)" }}>{m.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VaultPage({ onToast, onNavigate }) {
  const [strategies, setStrategies] = React.useState([]);
  const [cat, setCat]               = React.useState("trading");
  const [loading, setLoading]       = React.useState(true);
  const [error, setError]           = React.useState(null);
  // Publish flow state
  const [publishTarget, setPublishTarget] = React.useState(null);
  const [myListings, setMyListings]       = React.useState([]);
  const [listingsLoading, setListingsLoading] = React.useState(true);

  React.useEffect(()=>{ loadStrategies(); loadMyListings(); },[]);

  async function loadMyListings() {
    try {
      const sb = window.sbClient;
      const { data: { user } } = await sb.auth.getUser();
      if (!user) { setListingsLoading(false); return; }
      const { data, error } = await sb
        .from("strategy_listings")
        .select("id, strategy_id, title, status, visibility, price_monthly, subscriber_count, view_count, created_at")
        .eq("creator_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setMyListings(data || []);
    } catch (e) { console.warn("[vault] listings load failed", e); }
    setListingsLoading(false);
  }

  async function deleteStrategy(s) {
    if (!s?.realId) return;
    if (!window.confirm(`Delete "${s.name}"? Backtest / optimize / stress runs linked to this strategy will be removed too. This cannot be undone.`)) return;
    try {
      const { error } = await window.sbClient.from("strategies").delete().eq("id", s.realId);
      if (error) {
        onToast?.("Couldn't delete '" + s.name + "': " + (error.message || 'unknown error'));
        return;
      }
      // Only remove from local state after Supabase confirms success
      setStrategies(prev => prev.filter(x => x.realId !== s.realId));
      onToast?.(`Deleted "${s.name}"`);
    } catch (e) {
      onToast?.("Couldn't delete '" + s.name + "': " + (e.message || 'unknown error'));
    }
  }

  async function toggleListingStatus(listing) {
    const next = listing.status === "live" ? "paused" : "live";
    try {
      const { error } = await window.sbClient
        .from("strategy_listings").update({ status: next }).eq("id", listing.id);
      if (error) throw error;
      setMyListings(prev => prev.map(l => l.id === listing.id ? { ...l, status: next } : l));
      onToast?.(`Listing ${next === "live" ? "published" : "paused"}`);
    } catch (e) { onToast?.(`Failed: ${e.message}`); }
  }

  async function loadStrategies() {
    setError(null); setLoading(true);
    try {
      const sb = window.sbClient;
      const { data: { user } } = await sb.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: rows, error: qErr } = await sb
        .from("strategies").select("*")
        .eq("user_id", user.id).order("created_at",{ascending:false});
      if (qErr) throw qErr;
      if (!rows || rows.length === 0) { setStrategies([]); setLoading(false); return; }

      // Batch: most-recent run row per strategy in a single round-trip
      const { data: runs } = await sb
        .from("backtest_runs")
        .select("id, strategy_id, metrics, equity_curve, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      const latest = {};
      (runs || []).forEach(r => { if (r.strategy_id && !latest[r.strategy_id]) latest[r.strategy_id] = r; });

      const mapped = rows.map(s => {
        const run = latest[s.id];
        const m   = run?.metrics || {};
        const spark = window.sparkFromEquity(run?.equity_curve);
        // 30-day window: last ~30 points of equity curve, return %
        let d30 = null;
        if (run?.equity_curve && run.equity_curve.length > 1) {
          const ec = run.equity_curve;
          const slice = ec.slice(Math.max(0, ec.length - 30));
          if (slice.length >= 2 && slice[0].v) d30 = (slice[slice.length-1].v / slice[0].v) - 1;
        }
        return {
          id: s.id, realId: s.id, name: s.name, status: s.status || "saved",
          perf: run ? pct(m.totalReturn ?? m.cagr) : (s.performance_metrics?.cagr || "—"),
          trades: m.tradeCount ?? s.performance_metrics?.totalTrades ?? 0,
          profit: s.performance_metrics?.totalPnL || "—",
          winRate: m.winRate != null ? Math.round(m.winRate * 100) : (s.performance_metrics?.winRate || 0),
          cat: "trading",
          sharpe: run ? num(m.sharpe) : "—",
          dd:     run ? pct(m.maxDrawdown, 1) : "—",
          d30:    d30 != null ? pct(d30, 1) : "—",
          color:  "#11ff99",
          sparkData: spark,
          latestRunId: run?.id || null,
        };
      });
      setStrategies(mapped);
    } catch (e) {
      // Soft-fail: keep mock data, surface a banner so the user knows it's stale
      console.warn("[vault] load failed, using mock", e);
      setError(e);
    }
    setLoading(false);
  }

  const filtered = strategies.filter(s=>s.cat===cat);
  const TABS=[{id:"trading",l:"Trading"},{id:"licensed",l:"Licensed"},{id:"earning",l:"Earning"}];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <PageHdr icon={Wallet} iconColor="var(--green)" iconBg="var(--green-dim)"
        title="My strategy vault"
        subtitle="Your portfolio, your strategies, your edge."
        action={<button className="btn btn-ghost btn-sm" onClick={()=>onNavigate?.("exchanges")}><Plus size={12}/> Connect exchange</button>}
      />

      {error && (
        <div style={{
          background:"rgba(255,32,71,0.06)", border:"1px solid rgba(255,32,71,0.25)",
          borderRadius:"var(--r-md)", padding:"10px 14px",
          display:"flex", justifyContent:"space-between", alignItems:"center", gap:8,
          fontFamily:"var(--font-ui)", fontSize:12, color:"var(--red)",
        }}>
          <span>
            <b>Couldn't load your strategies.</b> Showing sample data instead. ({error.message || "unknown error"})
          </span>
          <button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"3px 10px"}} onClick={()=>{ setError(null); loadStrategies(); }}>
            Retry
          </button>
        </div>
      )}

      {/* Ask GQ */}
      <div style={{ background:"rgba(217,70,239,0.06)", border:"1px solid rgba(217,70,239,0.20)", borderRadius:12, padding:20, display:"flex", alignItems:"center", justifyContent:"space-between", gap:16, flexWrap:"wrap" }}>
        <div>
          <div style={{ fontFamily:"'Inter Tight', 'Inter', sans-serif", fontSize:22, color:"#D946EF", fontWeight:800, letterSpacing:"-0.025em" }}>Ask GQ™</div>
          <div style={{ fontFamily:"var(--font-ui)", fontSize:13, color:"var(--muted)", marginTop:4 }}>Describe a strategy idea and let AI build, backtest and optimise it.</div>
        </div>
        <button className="btn btn-primary" onClick={()=>window.dispatchEvent(new CustomEvent("gq:open-chat", { detail: { seed: "Suggest a strategy I should build given my current portfolio." } }))}>Generate a strategy</button>
      </div>

      {/* Stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(170px,1fr))", gap:10 }}>
        <StatCard title="Total strategies" value={strategies.length} sub="In your vault" icon={Wallet} accent="default"/>
        <StatCard title="Active strategies" value={strategies.filter(s=>s.status==="active"||s.status==="saved").length} sub="Trading & saved" icon={TrendingUp} accent="green"/>
        <StatCard title="Avg Sharpe" value={(() => { const sh = strategies.filter(s=>s.sharpe&&s.sharpe!=="—").map(s=>parseFloat(s.sharpe)).filter(v=>!isNaN(v)); return sh.length ? (sh.reduce((a,b)=>a+b,0)/sh.length).toFixed(2) : "—"; })()} sub="Across backtested" icon={Activity} accent="purple"/>
        <StatCard title="Listings" value={myListings.length} sub={`${myListings.filter(l=>l.status==="live").length} live`} icon={Crown} accent="cyan"/>
      </div>

      {/* Strategies */}
      <div className="card">
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, flexWrap:"wrap", gap:10 }}>
          <span style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)" }}>My strategies</span>
          <div style={{ display:"flex", gap:6 }}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setCat(t.id)} style={{
                background:cat===t.id?"var(--primary-dim)":"transparent",
                border:"1px solid",borderColor:cat===t.id?"rgba(217,70,239,.3)":"transparent",
                borderRadius:"var(--r-full)",padding:"4px 12px",cursor:"pointer",
                fontFamily:"var(--font-ui)",fontSize:12,fontWeight:cat===t.id?600:400,
                color:cat===t.id?"var(--primary)":"var(--muted)",
              }}>{t.l} ({strategies.filter(s=>s.cat===t.id).length})</button>
            ))}
          </div>
        </div>
        {loading ? (
          <SkeletonList rows={3}/>
        ) : filtered.length > 0 ? (
          filtered.map(s=><StratRow key={s.id} s={s} onToast={onToast} onNavigate={onNavigate} onPublish={setPublishTarget} onDelete={deleteStrategy}/>)
        ) : cat === "trading" ? (
          <EmptyState
            icon={Wallet}
            title="Your vault is empty."
            body="Mint your first strategy or describe one to GQ — we'll backtest it and you can deploy from here."
            primary={{ label: "Mint a strategy", icon: Plus, onClick: () => onNavigate?.("mint") }}
            secondary={{ label: "Describe with GQ", icon: Sparkles, onClick: () => window.dispatchEvent(new CustomEvent("gq:open-chat", { detail: { seed: "Help me design a new trading strategy." } })) }}
          />
        ) : cat === "licensed" ? (
          <EmptyState
            icon={Crown}
            title="No licensed strategies yet."
            body="Browse the marketplace to subscribe to strategies built by other quants. Their performance lives alongside yours."
            primary={{ label: "Open marketplace", icon: Store, onClick: () => onNavigate?.("marketplace") }}
          />
        ) : (
          <EmptyState
            icon={DollarSign}
            title="No strategies listed."
            body="List one of your trading strategies on the marketplace and earn subscription revenue from other members."
            primary={{ label: "List a strategy", icon: Plus, onClick: () => onNavigate?.("marketplace") }}
          />
        )}
      </div>

      {/* My listings */}
      {!listingsLoading && myListings.length > 0 && (
        <div className="card">
          <div className="card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>My marketplace listings</span>
            <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{myListings.length} · {myListings.filter(l=>l.status==="live").length} live</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:0,marginTop:8}}>
            {myListings.map((l, i) => (
              <div key={l.id} style={{
                display:"grid", gridTemplateColumns:"1fr 70px 100px 110px auto", gap:14,
                padding:"10px 0", alignItems:"center",
                borderBottom: i < myListings.length-1 ? "1px solid var(--border)" : "none",
              }}>
                <span style={{fontFamily:"var(--font-ui)",fontSize:13,color:"var(--ink)",fontWeight:500}}>{l.title}</span>
                <span className={`badge ${l.status==="live" ? "badge-green" : l.status==="paused" ? "badge-muted" : "badge-yellow"}`} style={{fontSize:10,justifySelf:"start"}}>{l.status}</span>
                <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>${l.price_monthly}/mo</span>
                <span style={{fontFamily:"var(--font-mono)",fontSize:11,color:"var(--muted)"}}>{l.subscriber_count||0} subs · {l.view_count||0} views</span>
                <div style={{display:"flex",gap:6,justifyContent:"flex-end"}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:11,padding:"3px 10px"}} onClick={()=>toggleListingStatus(l)}>
                    {l.status === "live" ? "Pause" : "Resume"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Publish modal */}
      {window.PublishModal && (
        <PublishModal
          strategy={publishTarget}
          open={!!publishTarget}
          onClose={() => setPublishTarget(null)}
          onPublished={() => { loadMyListings(); }}
          onToast={onToast}
        />
      )}

      {/* Quick nav */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))", gap:8 }}>
        {[
          {id:"tearsheet",l:"Performance reports",ic:FileText,c:"var(--primary)"},
          {id:"designer",  l:"Strategy lab",         ic:Layers,   c:"var(--yellow)"},
          {id:"optimize",  l:"GQ™ Optimize",        ic:Cpu,      c:"var(--primary)"},
          {id:"stress",    l:"Stress test",          ic:Shield,   c:"var(--orange)"},
        ].map(f=>{
          const Ic=f.ic;
          return(
            <button key={f.id} onClick={()=>onNavigate(f.id)} style={{ background:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:10, padding:"14px", cursor:"pointer", textAlign:"left", transition:"border-color .15s" }}>
              <Ic size={16} color={f.c}/>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:12, fontWeight:500, color:"var(--ink)", marginTop:8 }}>{f.l}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
window.VaultPage = VaultPage;
